import React from 'react';

const CLASS_STYLES: Record<string, string> = {
  SMR: 'bg-green-900 text-green-300 border border-green-700',
  MACRO_LIQ: 'bg-yellow-900 text-yellow-300 border border-yellow-700',
  MACRO_ONLY: 'bg-orange-900 text-orange-300 border border-orange-700',
  BREAKER_ONLY: 'bg-gray-700 text-gray-300 border border-gray-500',
  ORPHAN: 'bg-red-900 text-red-200 border border-red-500 animate-pulse',
  ORPHAN_DUPLICATE: 'bg-red-950 text-red-300 border border-red-800',
  TREND_CONT: 'bg-purple-900 text-purple-300 border border-purple-700',
  SECOND_CHANCE: 'bg-blue-900 text-blue-300 border border-blue-700',
  BIAS_SNIPE: 'bg-cyan-950 text-cyan-500 border border-dashed border-cyan-800',
  DAILY_TREND: 'bg-indigo-950 text-indigo-400 border border-dashed border-indigo-800',
};

// Shadow observers: paper models that log virtual entries/exits to risk_log
// only -- no real orders. Badges get a dashed border + SHADOW pill.
export const SHADOW_CLASSES = new Set(['BIAS_SNIPE', 'DAILY_TREND']);

const CLASS_TIPS: Record<string, string> = {
  SMR: 'Full setup: HTF liquidity + SMT divergence + breaker + macro window. Highest probability.',
  MACRO_LIQ: 'Breaker + HTF liquidity + macro window. No SMT required. Half size.',
  MACRO_ONLY: 'Breaker + macro window only. No liquidity or SMT. Half size.',
  BREAKER_ONLY: 'Breaker alone. No macro, liquidity, or SMT. Logged only, no trade.',
  ORPHAN: 'Untracked Tradovate position detected by orphan recovery. Protected with a conservative stop (or auto-flattened if no stop could be placed). Manual review required.',
  ORPHAN_DUPLICATE: 'Phantom duplicate trade row created by a flatten fill-matching edge case. Not a real second position -- safe to ignore in P&L, flagged for cleanup.',
  TREND_CONT: 'Trend-continuation entry: sustained one-directional move without a qualifying counter-swing expansion. Retest-confirmation into a fresh short-lookback zone, no SMT, capped at 3/4 criteria (1 contract).',
  SECOND_CHANCE: 'Re-arm entry: second chance at a stopped-out zone within the re-arm window. 1 contract, 1.5R fixed target, max 2/day, kill-switch guarded.',
  BIAS_SNIPE: 'SHADOW OBSERVER — not a real trade. Daily-bias sweep-snipe model logging virtual entries/exits for evaluation. No orders are placed; P&L is hypothetical.',
  DAILY_TREND: 'SHADOW OBSERVER — not a real trade. Hybrid daily-bias trend model logging virtual entries/exits for evaluation. No orders are placed; P&L is hypothetical.',
};

const MODE_LABELS: Record<string, string> = {
  confirmation: 'Conf',
  retest_any: 'Retest',
  retest_deep: 'Deep',
};

const MODE_TIPS: Record<string, string> = {
  confirmation: 'Confirmation entry: entered on the close of the candle that confirmed the breaker pattern.',
  retest_any: 'Retest entry: entered when price pulled back anywhere into the breaker zone after confirmation.',
  retest_deep: 'Deep retest: entered when price pulled back to equilibrium (50%+) of the breaker zone. Highest R:R.',
};

export function ClassBadge({ signalClass }: { signalClass?: string }) {
  if (!signalClass) return <span className="text-gray-600 text-xs">—</span>;
  const style = CLASS_STYLES[signalClass] || 'bg-gray-700 text-gray-400';
  const tip = CLASS_TIPS[signalClass] || '';
  const isShadow = SHADOW_CLASSES.has(signalClass);
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`text-xs px-1.5 py-0.5 rounded font-bold cursor-help ${style}`} title={tip}>
        {signalClass}
      </span>
      {isShadow && (
        <span
          className="text-[9px] px-1 py-0.5 rounded font-bold tracking-wider bg-gray-800 text-gray-400 border border-dashed border-gray-600 cursor-help"
          title="Shadow observer: virtual model only — no real order was placed."
        >
          SHADOW
        </span>
      )}
    </span>
  );
}

export function EntryModeBadge({ mode }: { mode?: string }) {
  if (!mode) return null;
  const tip = MODE_TIPS[mode] || '';
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 ml-1 cursor-help" title={tip}>
      {MODE_LABELS[mode] || mode}
    </span>
  );
}

const SKIP_EXACT = new Set(['autoexecute_disabled', 'classifier_skip']);
const SKIP_PREFIXES = ['score_below_threshold', 'instrument_not_allowed', 'invalid_direction', 'missing_', 'bad_stop'];
const FAIL_PREFIXES = ['order_error', 'tradovate_error', 'exception'];

export function categorizeTradovate(executed: boolean, reason: string | null | undefined): 'live' | 'skip' | 'fail' | null {
  if (executed) return 'live';
  if (!reason) return null;
  if (SKIP_EXACT.has(reason) || SKIP_PREFIXES.some(p => reason.startsWith(p))) return 'skip';
  if (FAIL_PREFIXES.some(p => reason.startsWith(p))) return 'fail';
  return null;
}

export function TradovatePill({
  executed,
  reason,
  orderId,
  onCopy,
}: {
  executed?: boolean;
  reason?: string | null;
  orderId?: string | null;
  onCopy?: (msg: string) => void;
}) {
  const kind = categorizeTradovate(!!executed, reason);
  if (!kind) return null;
  const styles =
    kind === 'live' ? 'bg-green-900 text-green-300 border border-green-700' :
    kind === 'skip' ? 'bg-yellow-900 text-yellow-300 border border-yellow-700' :
    'bg-red-900 text-red-300 border border-red-700';
  const label = kind === 'live' ? 'LIVE' : kind === 'skip' ? 'SKIP' : 'FAIL';
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!orderId) return;
    navigator.clipboard?.writeText(orderId).then(
      () => onCopy?.(`Copied order ${orderId}`),
      () => onCopy?.('Copy failed'),
    );
  };
  return (
    <span
      onClick={handleClick}
      title={reason || ''}
      className={`text-xs px-1.5 py-0.5 rounded font-bold ml-1 inline-block ${styles} ${orderId ? 'cursor-pointer' : 'cursor-help'}`}
      style={{ maxWidth: 60, height: 20, lineHeight: '14px' }}
    >
      {label}
    </span>
  );
}

export function SmtChip({
  confirmed,
  strength15m,
  strength1m,
}: {
  confirmed?: boolean;
  strength15m?: string | null;
  strength1m?: string | null;
}) {
  if (!confirmed) return null;
  const s15 = strength15m || 'none';
  const s1 = strength1m || 'none';
  let label: string | null = null;
  let styles = '';
  if (s15 === 'dual') {
    label = 'SMT\u00b7Dual';
    styles = 'bg-yellow-600 text-black border border-yellow-500';
  } else if (s15 === 'es_only') {
    label = 'SMT\u00b7ES';
    styles = 'bg-gray-400 text-black border border-gray-300';
  } else if (s15 === 'ym_only') {
    label = 'SMT\u00b7YM';
    styles = 'bg-gray-400 text-black border border-gray-300';
  } else if (s15 === 'none' && (s1 === 'dual' || s1 === 'es_only' || s1 === 'ym_only')) {
    label = 'SMT\u00b71m';
    styles = 'bg-orange-700 text-orange-100 border border-orange-600';
  }
  if (!label) return null;
  return (
    <span
      title={`15m: ${s15} \u00b7 1m: ${s1}`}
      className={`text-xs px-1.5 py-0.5 rounded font-bold ml-1 cursor-help inline-block ${styles}`}
      style={{ maxWidth: 60, height: 20, lineHeight: '14px' }}
    >
      {label}
    </span>
  );
}

export function FormationSpeedPill({ signal }: { signal: any }) {
  const raw = signal?.formation_speed_bars ?? signal?.raw_payload?.formation_speed_bars;
  if (raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  let styles = '';
  if (n <= 5)       styles = 'bg-green-900/40 text-green-300 border border-green-700';
  else if (n <= 10) styles = 'bg-blue-900/40 text-blue-300 border border-blue-700';
  else if (n <= 19) styles = 'bg-gray-700/40 text-gray-300 border border-gray-600';
  else              styles = 'bg-red-900/40 text-red-300 border border-red-700';
  return (
    <span
      title={`Formation speed: ${n} bars from prior pivot to breaker confirmation`}
      className={`text-xs px-1.5 py-0.5 rounded font-bold ml-1 cursor-help inline-block ${styles}`}
      style={{ height: 20, lineHeight: '14px' }}
    >
      {n}b
    </span>
  );
}

export function HtfDistancePill({ signal }: { signal: any }) {
  const raw = signal?.distance_from_htf_points ?? signal?.raw_payload?.distance_from_htf_points;
  if (raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0 || n >= 99000) return null;
  let styles = '';
  if (n <= 5)       styles = 'bg-green-900/40 text-green-300 border border-green-700';
  else if (n <= 10) styles = 'bg-blue-900/40 text-blue-300 border border-blue-700';
  else if (n <= 25) styles = 'bg-gray-700/40 text-gray-300 border border-gray-600';
  else              styles = 'bg-red-900/40 text-red-300 border border-red-700';
  return (
    <span
      title={`Distance to nearest unswept HTF level: ${n} pts`}
      className={`text-xs px-1.5 py-0.5 rounded font-bold ml-1 cursor-help inline-block ${styles}`}
      style={{ height: 20, lineHeight: '14px' }}
    >
      {n}pt
    </span>
  );
}

export function QualityScore({ score }: { score?: number }) {
  if (score == null) return <span className="text-gray-600 text-xs">—</span>;
  const color =
    score >= 75 ? 'text-green-400' :
    score >= 50 ? 'text-yellow-400' :
    score >= 30 ? 'text-orange-400' :
    'text-gray-500';
  return <span className={`font-mono text-xs font-bold cursor-help ${color}`} title={`Quality score: ${score}/100. Higher = more confluence factors present.`}>{score}</span>;
}
